import { IconButton } from "@ai-teach/ui-web";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { HiPause, HiPlay, HiX } from "react-icons/hi";
import { MdAddCircleOutline } from "react-icons/md";

export interface VideoPlayerHandle {
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
}

export interface VideoPlayerProps {
  src: string;
  onRemove?: () => void;
  addCheckpoint?: (timestamp: number) => void;
  checkpoints?: { timestamp: number; index: number; skipTo?: number | null }[];
  onCheckpointClick?: (index: number) => void;
  onDurationChange?: (duration: number) => void;
  onTimeUpdate?: (currentTime: number) => void;
  isPreview?: boolean;
}

export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer(
  {
    src,
    onRemove,
    addCheckpoint,
    checkpoints,
    onCheckpointClick,
    onDurationChange,
    onTimeUpdate,
    isPreview,
  },
  ref
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSkippedCheckpointRef = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    play: () => {
      videoRef.current?.play();
      setIsPlaying(true);
    },
    pause: () => {
      videoRef.current?.pause();
      setIsPlaying(false);
    },
    seek: (time: number) => {
      if (videoRef.current) videoRef.current.currentTime = time;
    },
  }));

  const handlePlayVideo = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateProgress = () => {
      const ct = video.currentTime;

      if (checkpoints) {
        for (const cp of checkpoints) {
          if (
            cp.skipTo != null &&
            cp.skipTo > cp.timestamp &&
            ct >= cp.timestamp &&
            ct < cp.skipTo
          ) {
            if (lastSkippedCheckpointRef.current !== cp.timestamp) {
              lastSkippedCheckpointRef.current = cp.timestamp;
              video.currentTime = cp.skipTo;
              return;
            }
            return;
          }
        }
        lastSkippedCheckpointRef.current = null;
      }

      setProgress((ct / video.duration) * 100);
      onTimeUpdate?.(ct);
    };

    video.addEventListener("timeupdate", updateProgress);

    return () => video.removeEventListener("timeupdate", updateProgress);
  });

  // Get video duration
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      onDurationChange?.(video.duration);
    };
    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => video.removeEventListener("loadedmetadata", handleLoadedMetadata);
  }, [onDurationChange]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const formatTimeWithDecimal = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = (time % 60).toFixed(1).padStart(4, "0");
    return `${minutes}:${seconds}`;
  };

  const handleProgressBar = (value: number) => {
    if (!videoRef.current) return;

    videoRef.current.currentTime = (value / 100) * videoRef.current.duration;
    setProgress(value);
  };

  return (
    <div onClick={() => handlePlayVideo()} className="relative w-full h-full bg-black group">
      <video ref={videoRef} src={src} className="w-full h-full" controls={false}>
        <track kind="captions" srcLang="en" label="English" default />
      </video>

      {addCheckpoint && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            addCheckpoint(videoRef.current?.currentTime ?? 0);
          }}
          className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/50 hover:bg-black backdrop-blur-sm border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-all duration-150 active:scale-95"
        >
          <MdAddCircleOutline className="text-sm shrink-0" />
          <span className="text-[10px] font-semibold tracking-wide">
            + {formatTimeWithDecimal(videoRef.current?.currentTime ?? 0)}
          </span>
        </button>
      )}

      {onRemove && (
        <IconButton
          type="button"
          variant="danger"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-2 right-2"
        >
          <HiX className="text-xs" />
        </IconButton>
      )}

      {/* play button */}
      {!isPreview && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handlePlayVideo();
          }}
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-lg bg-white/90 hover:bg-white flex items-center justify-center transition-all ${isPlaying && "opacity-0 group-hover:opacity-90"}`}
        >
          {isPlaying ? (
            <HiPause className="text-3xl text-black" />
          ) : (
            <HiPlay className="text-3xl text-black" />
          )}
        </button>
      )}

      {/* Progress bar */}
      {!isPreview && (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={`absolute bottom-0 left-0 right-0 flex items-center gap-2 px-3 py-2 bg-black/60 transition-opacity ${isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}
        >
          <span className="text-xs text-white font-mono">
            {formatTime(videoRef.current?.currentTime ?? 0)}
          </span>
          <div className="flex-1 relative flex items-center">
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => handleProgressBar(Number(e.target.value))}
              className="w-full h-1 accent-white cursor-pointer"
            />
            {duration > 0 &&
              checkpoints?.map((point) =>
                point.skipTo != null && point.skipTo > point.timestamp ? (
                  <div
                    key={`skipto-${point.index}`}
                    className="absolute h-1.5 bg-red-500/60 pointer-events-none rounded-sm"
                    style={{
                      left: `${(point.timestamp / duration) * 100}%`,
                      width: `${((point.skipTo - point.timestamp) / duration) * 100}%`,
                    }}
                  />
                ) : null
              )}
            {duration > 0 &&
              checkpoints?.map((point) => (
                <div
                  key={point.index}
                  className="absolute -translate-x-1/2 group/marker cursor-pointer"
                  style={{ left: `${(point.timestamp / duration) * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!videoRef.current) return;

                    if (isPlaying) {
                      videoRef.current.pause();
                      setIsPlaying(false);
                    }

                    videoRef.current.currentTime = point.timestamp;

                    onCheckpointClick?.(point.index);
                  }}
                >
                  <div className="w-3 h-3 rounded-full bg-accent-200 border-2 border-black/40 shadow-sm group-hover/marker:scale-125 transition-transform" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/marker:flex flex-col items-center pointer-events-none">
                    <span className="whitespace-nowrap text-[9px] font-semibold text-white bg-black/80 px-1.5 py-0.5 rounded">
                      {formatTime(point.timestamp)}
                    </span>
                    <div className="w-px h-1 bg-black/60" />
                  </div>
                </div>
              ))}
          </div>
          <span className="text-xs text-white font-mono">{formatTime(duration)}</span>
        </div>
      )}
    </div>
  );
});
